import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabaseClient';
import {
  SecurityIncident,
  IncidentSeverity,
  IncidentStatus,
  SecurityAlert,
  AuditSnapshot,
  SnapshotType,
  RestoreEvent,
  RestoreStatus,
  PaypalEnvironmentSnapshot,
} from './models';
import {
  fetchIncidents,
  createIncident,
  updateIncidentStatus,
  fetchAlerts,
  acknowledgeAlert,
  createAlert,
  fetchSnapshots,
  createSnapshot,
  fetchRestoreEvents,
  createRestoreEvent,
  updateRestoreEventStatus,
  fetchPaypalSnapshots,
} from './repository';

export function useSecurityIncidents(filters?: {
  severity?: IncidentSeverity;
  status?: IncidentStatus;
}) {
  const [incidents, setIncidents] = useState<SecurityIncident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    async function load() {
      try {
        const data = await fetchIncidents(filters);
        setIncidents(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [filters?.severity, filters?.status]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchIncidents(filters);
        setIncidents(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    }
    load();

    const channelName = `security-incidents-${Date.now()}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'security_incidents' },
        () => refetch()
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'security_incidents' },
        () => refetch()
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'security_incidents' },
        () => refetch()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [filters?.severity, filters?.status]);

  const updateStatus = useCallback(
    async (id: string, status: IncidentStatus, resolvedBy?: string) => {
      await updateIncidentStatus(id, status, resolvedBy);
      refetch();
    },
    [refetch]
  );

  return { incidents, loading, error, refetch, createIncident, updateStatus };
}

export function useSecurityAlerts(onlyActive?: boolean) {
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    async function load() {
      try {
        const data = await fetchAlerts(onlyActive);
        setAlerts(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [onlyActive]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchAlerts(onlyActive);
        setAlerts(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    }
    load();

    const channelName = `security-alerts-${Date.now()}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'security_alerts' },
        () => refetch()
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'security_alerts' },
        () => refetch()
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'security_alerts' },
        () => refetch()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [onlyActive]);

  const acknowledgeAlertCallback = useCallback(
    async (id: string, userId: string) => {
      await acknowledgeAlert(id, userId);
      refetch();
    },
    [refetch]
  );

  return {
    alerts,
    loading,
    error,
    refetch,
    acknowledgeAlert: acknowledgeAlertCallback,
    createAlert,
  };
}

export function useAuditSnapshots(type?: SnapshotType) {
  const [snapshots, setSnapshots] = useState<AuditSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    async function load() {
      try {
        const data = await fetchSnapshots(type);
        setSnapshots(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [type]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchSnapshots(type);
        setSnapshots(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    }
    load();

    const channelName = `audit-snapshots-${Date.now()}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'audit_snapshots' },
        () => refetch()
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'audit_snapshots' },
        () => refetch()
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'audit_snapshots' },
        () => refetch()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [type]);

  return { snapshots, loading, error, refetch, createSnapshot };
}

export function useRestoreEvents() {
  const [restoreEvents, setRestoreEvents] = useState<RestoreEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    async function load() {
      try {
        const data = await fetchRestoreEvents();
        setRestoreEvents(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchRestoreEvents();
        setRestoreEvents(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    }
    load();

    const channelName = `restore-events-${Date.now()}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'restore_events' },
        () => refetch()
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'restore_events' },
        () => refetch()
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'restore_events' },
        () => refetch()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const updateStatus = useCallback(
    async (id: string, status: RestoreStatus, integrityCheck?: Record<string, unknown>) => {
      await updateRestoreEventStatus(id, status, integrityCheck);
      refetch();
    },
    [refetch]
  );

  return {
    restoreEvents,
    loading,
    error,
    refetch,
    createRestoreEvent,
    updateStatus,
  };
}

export function usePaypalSnapshots() {
  const [snapshots, setSnapshots] = useState<PaypalEnvironmentSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    async function load() {
      try {
        const data = await fetchPaypalSnapshots();
        setSnapshots(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchPaypalSnapshots();
        setSnapshots(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    }
    load();

    const channelName = `paypal-environment-snapshots-${Date.now()}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'paypal_environment_snapshots' },
        () => refetch()
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'paypal_environment_snapshots' },
        () => refetch()
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'paypal_environment_snapshots' },
        () => refetch()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return { snapshots, loading, error, refetch };
}
